import i18n from '@/utils/i18n';
import React, { useEffect, useRef, useState } from 'react';
import {
  NativeSyntheticEvent,
  StyleProp,
  TextInput,
  TextInputKeyPressEventData,
  ViewStyle,
} from 'react-native';
import { SearchInput } from './search-input';
import { useDebouncedCallback } from 'use-debounce';
import { useToasts } from '@/contexts/toast-context';

const DEBOUNCE_DELAY_MS = 500;

type TextInputSelection =
  | {
      end: number;
      start: number;
    }
  | undefined;

interface Props {
  autoFocus?: boolean;
  clearSearch: () => void;
  containerStyle?: StyleProp<ViewStyle>;
  initialSearchText?: string;
  inputRef?: React.RefObject<TextInput | null>;
  loading?: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
  onKeyPress?: (
    event: NativeSyntheticEvent<TextInputKeyPressEventData>,
  ) => void;
  onSearchItems: (query: string) => Promise<void>;
  onSearchTextChange?: (text: string) => void;
  placeholder?: string;
  resetSelectAllText?: () => void;
  selectAllText?: boolean;
}

export const ItemSearchInput: React.FC<Props> = ({
  autoFocus = true,
  clearSearch,
  containerStyle,
  initialSearchText = '',
  inputRef,
  loading,
  onBlur,
  onFocus,
  onSearchItems,
  onKeyPress,
  onSearchTextChange,
  placeholder = i18n.t('search_item_placeholder'),
  resetSelectAllText,
  selectAllText,
}) => {
  const { showErrorToast } = useToasts();

  const [searchPending, setSearchPending] = useState(false);
  const [searchText, setSearchText] = useState(initialSearchText);
  const [selection, setSelection] = useState<TextInputSelection>();

  const internalInputRef = useRef<TextInput>(null);
  const finalInputRef = inputRef || internalInputRef;

  // Restore search text when initialSearchText changes
  useEffect(() => {
    setSearchText((prev) =>
      prev !== initialSearchText ? initialSearchText : prev,
    );
  }, [initialSearchText]);

  useEffect(() => {
    if (selectAllText) {
      finalInputRef.current?.focus();
      setSelection({ end: searchText.length, start: 0 });
    }

    // Reset selection before effect runs again
    return () => {
      setSelection(undefined);
      resetSelectAllText?.();
    };
  }, [resetSelectAllText, selectAllText, searchText, finalInputRef]);

  const debouncedSearchItems = useDebouncedCallback(async (input) => {
    try {
      // Only update UI if this is still the latest input
      await onSearchItems(input);
    } catch (e) {
      showErrorToast();
    } finally {
      setSearchPending(false);
    }
  }, DEBOUNCE_DELAY_MS);

  const handleChangeText = (input: string) => {
    setSearchText(input);
    onSearchTextChange?.(input);
    if (input === '') {
      debouncedSearchItems.cancel();
      clearSearch();
      setSearchPending(false);
    } else if (input !== searchText) {
      // iOS issues an blur event which causes another search
      // so check if the input has changed value
      setSearchPending(true);
      debouncedSearchItems?.(input);
    }
  };

  return (
    <SearchInput
      autoFocus={autoFocus}
      containerStyle={containerStyle}
      inputRef={inputRef}
      isLoading={searchPending || loading}
      onBlur={onBlur}
      onChangeText={handleChangeText}
      onFocus={onFocus}
      onKeyPress={onKeyPress}
      placeholder={placeholder}
      selection={selection}
      testID="item-search-input"
      value={searchText}
    />
  );
};
